import dayjs from "dayjs";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { INextAndPreviousTrip, IOffer } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetPreviousAndNextTrainActionProps } from "../../../Orchestrator/Types/Interface/ActionParams/SearchBookingParams";
import { PRODUCT_TIER, ProductCodeEnum, ProductTier, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * Quick-exchange fare-tier fall-up.
 *
 * Tier-aware logic derives every code list from PRODUCT_TIER (single source
 * of truth). The chain falls UP only (never downgrade): a Saver source can
 * land on Value, but a Value source can't land on Saver. Same fare class
 * first, then escalate. Group-seat products (duo/quad) have no fall-up.
 */
const FALL_UP_BY_TIER: Record<ProductTier, ProductTier[]> = {
    [ProductTier.STANDARD_SAVER]: [
        ProductTier.STANDARD_VALUE,
        ProductTier.PREMIUM_SAVER,
        ProductTier.PREMIUM_VALUE,
    ],
    [ProductTier.STANDARD_VALUE]: [ProductTier.PREMIUM_VALUE],
    [ProductTier.PREMIUM_SAVER]: [ProductTier.PREMIUM_VALUE],
    [ProductTier.PREMIUM_VALUE]: [],
    [ProductTier.GROUP_SEAT]: [],
};

// Precomputed inverse of PRODUCT_TIER — one O(N) walk at module load so the
// hot path in selectExchangeOffersByTicketClass is a constant-time lookup.
const CODES_BY_TIER: Record<ProductTier, ProductCodeEnum[]> = {
    [ProductTier.STANDARD_SAVER]: [],
    [ProductTier.STANDARD_VALUE]: [],
    [ProductTier.PREMIUM_SAVER]: [],
    [ProductTier.PREMIUM_VALUE]: [],
    [ProductTier.GROUP_SEAT]: [],
};
(Object.keys(PRODUCT_TIER) as ProductCodeEnum[]).forEach((code) => {
    CODES_BY_TIER[PRODUCT_TIER[code]].push(code);
});

// Precomputed fall-up chain materialised to its destination code lists, so
// selectExchangeOffersByTicketClass needs only one array lookup per call
// (no per-call .map() allocation).
const FALL_UP_CODES_BY_TIER: Record<ProductTier, ProductCodeEnum[][]> = {
    [ProductTier.STANDARD_SAVER]: FALL_UP_BY_TIER[ProductTier.STANDARD_SAVER].map((t) => CODES_BY_TIER[t]),
    [ProductTier.STANDARD_VALUE]: FALL_UP_BY_TIER[ProductTier.STANDARD_VALUE].map((t) => CODES_BY_TIER[t]),
    [ProductTier.PREMIUM_SAVER]: FALL_UP_BY_TIER[ProductTier.PREMIUM_SAVER].map((t) => CODES_BY_TIER[t]),
    [ProductTier.PREMIUM_VALUE]: FALL_UP_BY_TIER[ProductTier.PREMIUM_VALUE].map((t) => CODES_BY_TIER[t]),
    [ProductTier.GROUP_SEAT]: FALL_UP_BY_TIER[ProductTier.GROUP_SEAT].map((t) => CODES_BY_TIER[t]),
};

// Combined Premium tier — used by the previous/next-train walk loops below
// to filter trains that carry at least one Premium offer when the source
// admission is on any Premium code.
const PREMIUM_TIER_CODES: string[] = [
    ...CODES_BY_TIER[ProductTier.PREMIUM_VALUE],
    ...CODES_BY_TIER[ProductTier.PREMIUM_SAVER],
];

/**
 * Walk a list of codes in priority order; return offers matching the FIRST
 * code that has any. Keeping this single-code-per-result is critical: the
 * caller sums exchangePrice + exchangeFee across all returned offers, so
 * returning two offers for the same trip (e.g., STV + SFF when both exist
 * in the same Standard Value tier) double-counts and inflates the delta.
 */
function pickFirstAvailableCode(offers: IOffer[], codes: string[]): IOffer[] {
    for (const code of codes) {
        const matched = offers.filter((o) => o.products[0]?.code === code);
        if (matched.length) return matched;
    }
    return [];
}

/**
 * Try the exact source code first, then siblings within the same tier, then
 * fall up to the configured higher tiers. Always returns at most one code
 * worth of offers (i.e., one offer per passenger per trip).
 */
function selectFromChain(
    offers: IOffer[],
    sourceCode: string,
    sameTier: string[],
    fallUpChain: string[][],
): IOffer[] {
    // 1. Same exact code first.
    const exact = offers.filter((o) => o.products[0]?.code === sourceCode);
    if (exact.length) return exact;
    // 2. Other codes in the same tier (excluding the source).
    const siblings = pickFirstAvailableCode(offers, sameTier.filter((c) => c !== sourceCode));
    if (siblings.length) return siblings;
    // 3. Fall-up tiers in order.
    for (const tierCodes of fallUpChain) {
        const matched = pickFirstAvailableCode(offers, tierCodes);
        if (matched.length) return matched;
    }
    return [];
}

function selectExchangeOffersByTicketClass(offers: IOffer[], nextBookingTicketTypeCode: string): IOffer[] {
    const sourceTier = PRODUCT_TIER[nextBookingTicketTypeCode as ProductCodeEnum];
    // Group-seat products (duo/quad) are out of scope for quick-exchange:
    // their fares are tied to fixed group sizes and don't have a meaningful
    // same-tier sibling swap. Returning [] here preserves the pre-tier-map
    // behaviour where the old if/else chain had no branch for group codes.
    if (sourceTier === undefined || sourceTier === ProductTier.GROUP_SEAT) return [];
    return selectFromChain(
        offers,
        nextBookingTicketTypeCode,
        CODES_BY_TIER[sourceTier],
        FALL_UP_CODES_BY_TIER[sourceTier],
    );
}

// Chip shows what the user actually pays at settlement. Mirrors the QE
// branch in PrepareBookingSummaryAction:
//
//   chip = max(0, exchangePrice − originalPrice) + exchangeFee
//
// `exchangePrice` is summed without the fee so the price-diff clamp doesn't
// silently consume the fee on a same-tier downgrade. The fee is then added
// back on top — always owed, regardless of how the price-diff lands.
// Tier-grouped selection above guarantees one offer per passenger per trip,
// so per-passenger fees aren't double-counted.
function sumExchangePrice(offers: IOffer[]): number {
    return offers.reduce((sum, o) => {
        const exchangePrice = o.exchangePrice ? calculatePrice(o.exchangePrice) : 0;
        return sum + exchangePrice;
    }, 0);
}

function sumExchangeFees(offers: IOffer[]): number {
    return offers.reduce((sum, o) => {
        const exchangeFee = o.exchangeFee ? calculatePrice(o.exchangeFee) : 0;
        return sum + exchangeFee;
    }, 0);
}

function applyExchangeMetadataToTrip(
    trip: INextAndPreviousTrip,
    selectedOffers: IOffer[],
    nextBookingPrice: number
): void {
    const priced = selectedOffers.filter((o) => o.exchangePrice);
    if (priced.length === 0) return;

    const newPriceTotal = sumExchangePrice(priced);
    const feeTotal = sumExchangeFees(priced);
    const priceDiff = newPriceTotal - nextBookingPrice;
    trip.exchangePrice = Math.max(0, priceDiff) + feeTotal;

    const ids = priced.map((o) => o.offerId);
    trip.exchangeOfferId = ids.length === 1 ? ids[0] : ids;
    trip.exchangeOfferPassengerRefs = priced.map((o) => ({
        offerId: o.offerId,
        passengerRefs: o.passengerRefs || [],
    }));
}

class GetPreviousAndNextTrainAction extends BaseAction<IDefaultActionResult, IGetPreviousAndNextTrainActionProps> {

    override async run(): Promise<IDefaultActionResult> {

        let actionResult: IDefaultActionResult = {
            status: "fail",
            data: null,
            message: ""
        }

        try {
            const { allBookedOffer, nextBookingTicketTypeCode, nextTicketStartDate, trains, nextBooking, inboundTrip, nextTicketTrainDirection } = this.params!            

            let previousOfferAndTrip: {
                offers: IOffer[];
                trip: INextAndPreviousTrip | null;
            } = {
                offers: [],
                trip: null
            }
            let nextOfferAndTrip: {
                offers: IOffer[];
                trip: INextAndPreviousTrip | null;
            } = {
                offers: [],
                trip: null
            }

            const nextTicketStartDateTime = new Date(nextTicketStartDate);
            const findAllThePreviousTrain: INextAndPreviousTrip[] = trains.filter((trip: INextAndPreviousTrip) => {
                const tripStartTime = new Date(trip.startTime);
                return nextTicketStartDateTime > tripStartTime
            })
            let previousTrain: INextAndPreviousTrip | null = null
            if (findAllThePreviousTrain.length > 1) {
                const sortedPreviousTrain = findAllThePreviousTrain
                    .filter(item => new Date(item.startTime) < nextTicketStartDateTime)
                    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                if (PREMIUM_TIER_CODES.includes(nextBookingTicketTypeCode)) {
                    /**
                     * Premium source (any Premium tier code): walk back until
                     * we find a previous train that has at least one Premium
                     * offer. Codes evolved beyond PFF — PRV / PFP / PRS are
                     * also Premium per the latest backend response.
                     */
                    for (let i = 0; i < sortedPreviousTrain.length; i++) {
                        const trip: INextAndPreviousTrip = sortedPreviousTrain[i];
                        const offers = allBookedOffer.filter((offer: IOffer) => trip.id === offer.tripCoverage.coveredTripId);
                        const getPremiumOffer = offers.find((offer: IOffer) => PREMIUM_TIER_CODES.includes(offer.products[0]?.code || ""));
                        if (getPremiumOffer) {
                            previousTrain = trip
                            break
                        }
                    }
                } else {
                    previousTrain = sortedPreviousTrain[0]
                }
            } else {
                if (findAllThePreviousTrain.length > 0) {
                    previousTrain = findAllThePreviousTrain[0]
                }
            }

            const findAllTheNextTrain: INextAndPreviousTrip[] = trains.filter((trip: INextAndPreviousTrip) => {
                const tripStartTime = new Date(trip.startTime);
                return nextTicketStartDateTime < tripStartTime
            })
            let nextTrain: INextAndPreviousTrip | null = null
            if (findAllTheNextTrain.length > 0) {
                if (PREMIUM_TIER_CODES.includes(nextBookingTicketTypeCode)) {
                    /**
                     * Premium source (any Premium tier code): walk forward
                     * until we find a next train that has at least one
                     * Premium offer. Same reasoning as the previous-train
                     * loop above.
                     */
                    for (let i = 0; i < findAllTheNextTrain.length; i++) {
                        const trip: INextAndPreviousTrip = findAllTheNextTrain[i];
                        const offers = allBookedOffer.filter((offer: IOffer) => trip.id === offer.tripCoverage.coveredTripId);
                        const getPremiumOffer = offers.find((offer: IOffer) => PREMIUM_TIER_CODES.includes(offer.products[0]?.code || ""));
                        if (getPremiumOffer) {
                            nextTrain = trip
                            break
                        }
                    }
                } else {
                    nextTrain = findAllTheNextTrain[0]
                }
            }

            /**
             * This is prevent issue, 
             * System Allows Booking Outbound Trip That Overlaps with Inbound Trip.
             * Next quick exchange train always less than the inbound start time.
             * We check next train starttime and endtime must be less than the inbound start time.
             * This need to check only for round trip.
            */
            if (inboundTrip && nextTrain) {
                const inboundStartTime: string = inboundTrip.startTime
                const nextTrainStartTime = nextTrain?.startTime
                const nextTrainEndTime = nextTrain?.endTime

                if (dayjs(nextTrainStartTime).isAfter(dayjs(inboundStartTime))) {
                    nextTrain = null
                }
                else if (dayjs(nextTrainEndTime).isAfter(dayjs(inboundStartTime))) {
                    nextTrain = null
                }
            }

            if (previousTrain) {
                previousOfferAndTrip.trip = previousTrain;
                const offers = allBookedOffer.filter((offer: IOffer) => previousTrain.id === offer.tripCoverage.coveredTripId);
                if (offers.length > 0) {
                    previousOfferAndTrip.offers = selectExchangeOffersByTicketClass(offers, nextBookingTicketTypeCode);
                }
            }
            if (nextTrain) {
                nextOfferAndTrip.trip = nextTrain;
                const offers = allBookedOffer.filter((offer: IOffer) => nextTrain.id === offer.tripCoverage.coveredTripId);
                if (offers.length > 0) {
                    nextOfferAndTrip.offers = selectExchangeOffersByTicketClass(offers, nextBookingTicketTypeCode);
                }
            }
            if (previousOfferAndTrip.offers.length > 0 && previousOfferAndTrip.trip != null) {
                let previousBookingPrice = 0;
                if (nextTicketTrainDirection === TripTypeEnum.OUT_BOUND) {
                    previousBookingPrice = nextBooking?.outboundConfirmedPrice || 0;
                } else {
                    previousBookingPrice = nextBooking?.inboundConfirmedPrice || 0;
                }
                applyExchangeMetadataToTrip(previousOfferAndTrip.trip, previousOfferAndTrip.offers, previousBookingPrice);
            }
            if (nextOfferAndTrip.offers.length > 0 && nextOfferAndTrip.trip != null) {
                let nextBookingPrice = 0;
                if (nextTicketTrainDirection === TripTypeEnum.OUT_BOUND) {
                    nextBookingPrice = nextBooking?.outboundConfirmedPrice || 0;
                } else {
                    nextBookingPrice = nextBooking?.inboundConfirmedPrice || 0;
                }
                applyExchangeMetadataToTrip(nextOfferAndTrip.trip, nextOfferAndTrip.offers, nextBookingPrice);
            }

            updateRecoilState("nextAndPreviousTrainAtom", {
                previousTrain: previousOfferAndTrip.offers.length > 0 ? previousOfferAndTrip.trip : null,
                nextTrain: nextOfferAndTrip.offers.length > 0 ? nextOfferAndTrip.trip : null
            })
            actionResult.data = {
                previousTrain: previousOfferAndTrip.trip,
                nextTrain: nextOfferAndTrip.trip
            }
            actionResult.status = "success";

        } catch (error: any) {
            console.error("Error fetching stops:", error);
            actionResult.message = "Something went wrong in GetPreviousAndNextTrainAction";
        } finally {
            return actionResult;
        }
    }
}

export const getPreviousAndNextTrainAction = new GetPreviousAndNextTrainAction()

